import React, { useMemo } from "react";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { formatDuration } from "../../../utils/formatDuration";
import { colors } from "../../../styles/theme";
import { CircularProgressChart, ChartSegment } from "./CircularProgressChart";
import { ChartLegend, LegendItem } from "./ChartLegend";
import { calculateTotal, addPercentages } from "../utils/calculations";

export interface EfficiencyData {
  percentage: number;
  distribution: {
    lessThan1d: number;
    oneTo2d: number;
    twoTo4d: number;
    fourTo7d: number;
    moreThan7d: number;
  };
}

interface EfficiencyChartProps {
  avgTimeToDecision: number;
  avgTimeToDecisionChange: number;
  efficiency: EfficiencyData;
  onLegendClick?: (segmentKey: string) => void;
}

export const EfficiencyChart: React.FC<EfficiencyChartProps> = ({
  avgTimeToDecision,
  avgTimeToDecisionChange,
  efficiency,
  onLegendClick,
}) => {
  // Convert distribution object to array, calculate total once, then add percentages
  const { distributionWithPercentages, total } = useMemo(() => {
    const distributionArray = [
      { key: "lessThan1d", count: efficiency.distribution.lessThan1d },
      { key: "oneTo2d", count: efficiency.distribution.oneTo2d },
      { key: "twoTo4d", count: efficiency.distribution.twoTo4d },
      { key: "fourTo7d", count: efficiency.distribution.fourTo7d },
      { key: "moreThan7d", count: efficiency.distribution.moreThan7d },
    ];
    const total = calculateTotal(distributionArray, "count");
    const withPercentages = addPercentages(distributionArray, "count", total);
    return { distributionWithPercentages: withPercentages, total };
  }, [efficiency.distribution]);

  // Extract percentages into object for easy access
  const percentages = useMemo(() => {
    return distributionWithPercentages.reduce((acc, item) => {
      acc[item.key] = item.percentage.toFixed(1);
      return acc;
    }, {} as Record<string, string>);
  }, [distributionWithPercentages]);

  const isPositiveChange = avgTimeToDecisionChange < 0; // Negative change is good (faster)
  const changePercentage =
    total > 0
      ? Math.abs(
          Math.round((avgTimeToDecisionChange / avgTimeToDecision) * 100)
        )
      : 0;

  // Color palette for the segments
  const segmentColors = {
    lessThan1d: "#b8e986", // Lighter green (best performance)
    oneTo2d: colors.brandPrimary, // Teal
    twoTo4d: "#f5a8b0", // Lighter coral
    fourTo7d: colors.brandOrange, // Orange
    moreThan7d: colors.brandCoral, // Coral (worst performance)
  };

  // Build segments for CircularProgressChart
  const segments: ChartSegment[] = [
    {
      key: "lessThan1d",
      percentage: parseFloat(percentages.lessThan1d),
      color: segmentColors.lessThan1d,
      label: "Same-Day Decisions",
      count: efficiency.distribution.lessThan1d,
    },
    {
      key: "oneTo2d",
      percentage: parseFloat(percentages.oneTo2d),
      color: segmentColors.oneTo2d,
      label: "Next-Day Decisions",
      count: efficiency.distribution.oneTo2d,
    },
    {
      key: "twoTo4d",
      percentage: parseFloat(percentages.twoTo4d),
      color: segmentColors.twoTo4d,
      label: "2-4 Day Decisions",
      count: efficiency.distribution.twoTo4d,
    },
    {
      key: "fourTo7d",
      percentage: parseFloat(percentages.fourTo7d),
      color: segmentColors.fourTo7d,
      label: "4-7 Day Decisions",
      count: efficiency.distribution.fourTo7d,
    },
    {
      key: "moreThan7d",
      percentage: parseFloat(percentages.moreThan7d),
      color: segmentColors.moreThan7d,
      label: "Week+ Decisions",
      count: efficiency.distribution.moreThan7d,
    },
  ];

  // Build legend items
  const legendItems: LegendItem[] = [
    {
      label: "Same-Day Decisions",
      color: segmentColors.lessThan1d,
      percentage: percentages.lessThan1d,
    },
    {
      label: "Next-Day Decisions",
      color: segmentColors.oneTo2d,
      percentage: percentages.oneTo2d,
    },
    {
      label: "2-4 Day Decisions",
      color: segmentColors.twoTo4d,
      percentage: percentages.twoTo4d,
    },
    {
      label: "4-7 Day Decisions",
      color: segmentColors.fourTo7d,
      percentage: percentages.fourTo7d,
    },
    {
      label: "Week+ Decisions",
      color: segmentColors.moreThan7d,
      percentage: percentages.moreThan7d,
    },
  ];

  // Handler for legend item clicks
  const handleLegendClick = (itemIndex: number) => {
    if (onLegendClick) {
      const segmentKey = distributionWithPercentages[itemIndex].key;
      onLegendClick(`decisioned_${segmentKey}_30d`);
    }
  };

  // Center content for the circular chart
  const centerContent = (
    <>
      <div
        style={{
          fontSize: "13px",
          color: "#8c8c8c",
          marginBottom: "6px",
        }}
      >
        Avg Time to Decision
      </div>
      <div
        style={{
          fontSize: "32px",
          fontWeight: "bold",
          color: "#262626",
          lineHeight: 1.2,
        }}
      >
        {formatDuration(avgTimeToDecision)}
      </div>
      <div
        style={{
          fontSize: "14px",
          fontWeight: 600,
          marginTop: "10px",
          color: isPositiveChange ? "#52c41a" : "#ff4d4f",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        {isPositiveChange ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
        {changePercentage}%
      </div>
      <div
        style={{
          fontSize: "12px",
          color: isPositiveChange ? "#52c41a" : "#ff4d4f",
          marginTop: "4px",
        }}
      >
        {isPositiveChange ? "-" : "+"}
        {formatDuration(Math.abs(avgTimeToDecisionChange))}
      </div>
    </>
  );

  return (
    <div
      style={{
        display: "flex",
        gap: "32px",
        alignItems: "center",
        padding: "20px 0",
      }}
    >
      {/* Left side: Circular Progress */}
      <div style={{ flex: "0 0 auto" }}>
        <CircularProgressChart
          segments={segments}
          centerContent={centerContent}
        />
      </div>

      {/* Right side: Legend */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <ChartLegend items={legendItems} onClick={handleLegendClick} />
      </div>
    </div>
  );
};
